import React, { useCallback, useEffect, useId, useState } from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../../utils/bookingApi';
import RecipientPicker from '../../../components/RecipientPicker';
import { PageHeader, Sheet, Skeleton, EmptyState, ErrorState, useConfirm, useToast } from '../../../components/ui';
import '../booking-shared.css';

const EMPTY_FORM = { name: '', recipientFilter: { type: 'all' } };

const FILTER_SUMMARY = {
  all: 'All members',
  role: f => `Role: ${f.role}`,
  active_membership: 'Active membership',
  pending_payment_membership: 'Overdue subscriptions',
  expiring_credits: 'Expiring credits',
  no_upcoming_bookings: 'No upcoming bookings',
  attended_recently: f => `Attended in the last ${Math.round((f.withinDays || 90) / 30)} months`,
  adhoc: f => `${f.userIds?.length ?? 0} specific people`,
};

function filterLabel(filter) {
  const val = FILTER_SUMMARY[filter?.type];
  return typeof val === 'function' ? val(filter) : (val || filter?.type || 'Unknown');
}

// Adding or changing a group: a name and who's in it, in a pop-up
function GroupSheet({ initial, onSave, onClose }) {
  const start = initial || EMPTY_FORM;
  const nameId = useId();
  const [form, setForm] = useState(start);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const changed = JSON.stringify(form) !== JSON.stringify(start);

  const submit = async () => {
    setNameError(null);
    if (!form.name.trim()) {
      setNameError('Give the group a name');
      setError('Give the group a name');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't save the group. Try again.");
      setSaving(false);
    }
  };

  return (
    <Sheet
      title={initial ? 'Edit group' : 'New group'}
      onClose={onClose}
      dirty={changed && !saving}
      onSubmit={submit}
      wide
      footer={requestClose => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="bk-label">
        <label htmlFor={nameId}>Group name</label>
        <input
          id={nameId}
          className="bk-input"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          maxLength={100}
          placeholder="For example, Tuesday regulars"
          aria-invalid={nameError ? 'true' : undefined}
          aria-describedby={nameError ? `${nameId}-error` : undefined}
        />
        {nameError && <span className="bk-field-error" id={`${nameId}-error`}>{nameError}</span>}
      </div>
      <p className="bk-label">Audience</p>
      <RecipientPicker
        value={form.recipientFilter}
        onChange={rf => setForm(f => ({ ...f, recipientFilter: rf || { type: 'all' } }))}
        groups={[]}
        forceEnabled
      />
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </Sheet>
  );
}

export default function AdminRecipientGroups() {
  const confirm = useConfirm();
  const toast = useToast();
  const [groups, setGroups] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(null);
  // null, 'new', or the group being changed
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoadError(false);
    return bookingApi.getRecipientGroups()
      .then(r => setGroups(r.data))
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    if (editing === 'new') await bookingApi.createRecipientGroup(form);
    else await bookingApi.updateRecipientGroup(editing.id, form);
    setEditing(null);
    toast(`${form.name.trim()} saved`);
    load();
  };

  const remove = async (group) => {
    const yes = await confirm({
      title: `Delete ${group.name}?`,
      message: "It's no longer offered when sending emails. Nobody is removed from the club.",
      confirmLabel: 'Delete',
    });
    if (!yes) return;
    setError(null);
    try {
      await bookingApi.deleteRecipientGroup(group.id);
      toast(`${group.name} deleted`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || `Couldn't delete ${group.name}. Try again.`);
    }
  };

  const newGroup = () => setEditing('new');

  return (
    <div className="bk-page bk-page--wide">
      <PageHeader
        back={{ to: '/admin-hub', label: 'Admin' }}
        title="Recipient groups"
        action={groups ? { label: 'New group', onClick: newGroup } : undefined}
      />

      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {loadError && <ErrorState title="Couldn't load recipient groups" onRetry={load} />}
      {!loadError && groups === null && <Skeleton rows={3} label="Loading recipient groups…" />}
      {groups?.length === 0 && (
        <EmptyState
          icon={UserGroupIcon}
          title="No saved groups yet"
          text="Save a group to reuse it when sending noticeboard emails."
        />
      )}

      {groups?.map(group => (
        <div key={group.id} className="bk-card">
          <strong>{group.name}</strong>
          <div className="bk-small">{filterLabel(group.recipientFilter)}</div>
          <div className="bk-row-actions">
            <button type="button" className="bk-btn bk-btn--sm" aria-label={`Edit ${group.name}`} onClick={() => setEditing(group)}>Edit</button>
            <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" aria-label={`Delete ${group.name}`} onClick={() => remove(group)}>Delete</button>
          </div>
        </div>
      ))}

      {editing && (
        <GroupSheet
          initial={editing === 'new' ? null : { name: editing.name, recipientFilter: editing.recipientFilter }}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
